import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The one thing flexbox can't do",
  "description": "",
  "published": "2016-09-07T00:00:00.000Z",
  "redirect_from": ["/blog/the-one-thing-flexbox-cant-do/swizec/7004"],
  "categories": "CSS, flexbox, HTML, Technical, Web Design and Development",
  "hero": "./img/1cdT7CL.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Ah yes, flexbox – that marvel of stylesheet technology that was going to absolve us, make our lives easier, and leave enough time for beers after work. How wonderful our future was to be!`}</p>
    <p>{`Imagine it: no more floats! No more strange CSS incantations with negative margins just to get some basic layout working! All those tricks you spent the last 10 years learning? Forget them. You don’t need them.`}</p>
    <p>{`Flexbox is here to save you!`}</p>
    <p>{`Want to `}<a parentName="p" {...{
        "href": "https://philipwalton.github.io/solved-by-flexbox/demos/vertical-centering/"
      }}>{`center vertically`}</a>{` in a container of unknown height? Flexbox can do it with a line of code! `}<em parentName="p">{`And`}</em>{` semantic HTML.`}</p>
    <p>{`With flexbox, vertical centering looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`.container {
  display: flex;
}

.centered {
  flex: 1;
}
`}</code></pre>
    <p>{`Assuming an HTML structure that looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<div class="container">
  <div class="centered">
    This is centered
  </div>
</div>
`}</code></pre>
    <p>{`Sure beats the best hack from a few years ago: 3 divs with 50% offset positioning. `}<a parentName="p" {...{
        "href": "#fn1-11043",
        "title": "see footnote"
      }}>{`1`}</a>{` The only thing harder for pure CSS than vertical centering is a 3-column design with equal-height columns. A good hack was never found.`}</p>
    <p>{`Until Flexbox!`}</p>
    <p>{`Want 3-columns that stretch proportionally to your spec? Say no more.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/vXONdj/"
      }}>{`3-column flexbox`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`Simple, isn’t it? Set the container up as a flexbox, then tell each child `}<inlineCode parentName="p">{`div`}</inlineCode>{` to take up an equal amount of space. Open it on Codepen, and play around with screen size. It all works.`}</p>
    <p>{`You can even turn it into rows without changing HTML. Like this:`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/gwpazQ/"
      }}>{`3-row flexbox`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <h2 {...{
      "id": "row-span-in-flexbox"
    }}>{`Row span in flexbox`}</h2>
    <p>{`But then your designer comes up with this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "740px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/5dbe4f301abd106c4838076d95af8650/50383/1cdT7CL.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "17.48878923766816%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'70\\'%20viewBox=\\'0%200%20400%2070\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M135%204v10l1%208h127V4l-64-1-64%201m134%201l-1%209%201%209h127V5l-63-1-64%201m0%2021v17l64%201h64V26l-64-1-64%201m-134%209l1%209h127V26H135v9M3%2058l1%209h126l1-2V50l-64-1H3v9m132%200l1%209h127V50l-64-1h-64v9m133-8v9l1%208h127V49h-63l-65%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5dbe4f301abd106c4838076d95af8650/ca0a1/1cdT7CL.webp 223w", "/static/5dbe4f301abd106c4838076d95af8650/75680/1cdT7CL.webp 445w", "/static/5dbe4f301abd106c4838076d95af8650/ca4a8/1cdT7CL.webp 740w"],
              "sizes": "(max-width: 740px) 100vw, 740px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5dbe4f301abd106c4838076d95af8650/e92b6/1cdT7CL.png 223w", "/static/5dbe4f301abd106c4838076d95af8650/e66bf/1cdT7CL.png 445w", "/static/5dbe4f301abd106c4838076d95af8650/50383/1cdT7CL.png 740w"],
              "sizes": "(max-width: 740px) 100vw, 740px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/5dbe4f301abd106c4838076d95af8650/50383/1cdT7CL.png",
              "alt": "1cdT7CL",
              "title": "1cdT7CL",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`It’s a grid of same-size buttons, and you are boned.`}</p>
    <p>{`You know the buttons have to be configurable, which means your code should be agnostic to their configuration. There can be more or less of them, their labels can vary, and their colors are configurable, too.`}</p>
    <p>{`Those buttons are data. You get an array of labels, and you have to render them as buttons.`}<a parentName="p" {...{
        "href": "#fn2-11043",
        "title": "see footnote"
      }}>{`2`}</a>{` Ideally, you could loop through them, make a list of buttons in HTML, and have CSS take care of layouting.`}</p>
    <p><inlineCode parentName="p">{`flex-flow: row wrap`}</inlineCode>{` is your friend. It breaks elements into lines and looks like this:`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/XjbrLP/"
      }}>{`6-grid flexbox `}</a>{`by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`That’s a grid – wasn’t hard at all. But your designer wants that first cell to span two rows.`}</p>
    <p>{`Flexbox row span. `}<em parentName="p">{`Now`}</em>{` you’re boned. If you take the naive approach like I did, the result looks like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "773px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/612f7/qgcOvyK.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "18.834080717488792%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'75\\'%20viewBox=\\'0%200%20400%2075\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M79%2026v23h103V3H79v23m109%200v23h102V3H188v23m108%200v23h102V3H296v23M3%2055l-1%209v8h89v-8l-1-9H3m282%200l-1%209v8h115v-8l-1-9c-1-1-112-2-113%200M96%2064v8h88V55H96v9m93-8v9l1%207h89V55h-45l-45%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/ca0a1/qgcOvyK.webp 223w", "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/75680/qgcOvyK.webp 445w", "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/c99d1/qgcOvyK.webp 773w"],
              "sizes": "(max-width: 773px) 100vw, 773px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/e92b6/qgcOvyK.png 223w", "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/e66bf/qgcOvyK.png 445w", "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/612f7/qgcOvyK.png 773w"],
              "sizes": "(max-width: 773px) 100vw, 773px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/ad228631cfe0f0f5e7e43aa8d6e60e4d/612f7/qgcOvyK.png",
              "alt": "qgcOvyK",
              "title": "qgcOvyK",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`?`}</p>
    <p>{`Maybe they won’t notice?`}</p>
    <p>{`Seriously, though, you can’t do this. There’s no such thing as a flexbox rowspan. You’ll have to suck it up and put your things into column elements.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/NRqGLQ/"
      }}>{`6-grid flexbox`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`Wrap each group of elements into another `}<inlineCode parentName="p">{`div`}</inlineCode>{`, set that div to `}<inlineCode parentName="p">{`display: flex`}</inlineCode>{`, tell it to `}<inlineCode parentName="p">{`flex-direction: column`}</inlineCode>{`, and make sure one of the elements contains only two elements. Give those elements different flex weights.`}</p>
    <p>{`Voila, flexbox rowspan. Not painful at all. ?`}</p>
    <hr></hr>
    <ol>
      <li parentName="ol"></li>
      <li parentName="ol">
        <p parentName="li">{`Many methods for vertical centering exist, but this has always been a panacea of CSS layouting. The hardest, most impossible thing to do. Google “CSS vertical centering” if you don’t believe me. `}<a parentName="p" {...{
            "href": "#fnr1-11043",
            "title": "return to article"
          }}>{`↩︎`}</a></p>
      </li>
      <li parentName="ol"></li>
    </ol>
    <p>{`4)  The real array has more than just labels, but let’s keep it simple. `}<a parentName="p" {...{
        "href": "#fnr2-11043",
        "title": "return to article"
      }}>{`↩︎`}</a></p>
    <p>{`5)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      